(ns arthur.audio.mix
  "Render independently placed audio tracks into one stage audio clock.

  The mix is derived from saved audio track leaves and immutable footage blobs.
  The transport still has ONE clock, so seeking, rate changes and looping stay
  tied to the same position the picture is drawn from.

  THE AUDIO BUFFER IS THE PRODUCT AND THE WAV IS ONE PACKAGING OF IT. `buffer!`
  renders and the wrappers below it package, rather than the render being spelled
  once per consumer.

  PLAYBACK NO LONGER PACKAGES AT ALL. It takes the buffer as it is — see
  `clock-source!` and `arthur.clock.graph` — because encoding a WAV so an
  `<audio>` element had a URL to hold cost O(the clip's length) on the main
  thread, on open, on every tab switch and on every edit to a track. The WAV is
  now what an EXPORT wants: bytes for an archive, or the buffer itself for a
  muxer. `clock!` below is the element backend's packaging and goes when it does."
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.clip :as clip]
            [arthur.domain.nest :as nest]
            [arthur.domain.node :as node]))

(defn wav-bytes
  "An `AudioBuffer` -> the bytes of a 16-bit PCM WAV.

  PEAK-NORMALISED ONLY IF IT WOULD CLIP. A mix of several tracks can sum past
  1.0, and 16-bit PCM has nowhere to put that, so the alternative to scaling is
  audible clipping on exactly the loudest moment. Below the threshold nothing is
  touched, so a single-track mix is the footage's own audio sample for sample."
  [^js buffer]
  (let [channels (.-numberOfChannels buffer)
        frames (.-length buffer)
        rate (.-sampleRate buffer)
        bytes (js/ArrayBuffer. (+ 44 (* frames channels 2)))
        view (js/DataView. bytes)
        samples (mapv #(.getChannelData buffer %) (range channels))
        ;; A HAND-WRITTEN LOOP over the typed arrays, not `(reduce max (for ...))`.
        ;; The lazy sequence that read beautifully allocated one boxed double per
        ;; SAMPLE — ten million of them for a three-minute mix — and spent the
        ;; whole of a sixteen-second project open walking them and collecting
        ;; them. Same arithmetic, no allocation.
        peak (loop [c 0 p 0]
               (if (< c channels)
                 (recur (inc c)
                        (let [^js data (nth samples c)]
                          (loop [i 0 p p]
                            (if (< i frames)
                              (recur (inc i)
                                     (let [a (js/Math.abs (aget data i))]
                                       (if (> a p) a p)))
                              p))))
                 p))
        level (if (> peak 0.98) (/ 0.98 peak) 1)]
    (doseq [[offset word] [[0 "RIFF"] [8 "WAVE"] [12 "fmt "] [36 "data"]]]
      (dotimes [i 4] (.setUint8 view (+ offset i) (.charCodeAt word i))))
    (.setUint32 view 4 (- (.-byteLength bytes) 8) true)
    (.setUint32 view 16 16 true)
    (.setUint16 view 20 1 true)
    (.setUint16 view 22 channels true)
    (.setUint32 view 24 rate true)
    (.setUint32 view 28 (* rate channels 2) true)
    (.setUint16 view 32 (* channels 2) true)
    (.setUint16 view 34 16 true)
    (.setUint32 view 40 (* frames channels 2) true)
    ;; Channel-outer so the channel's array is looked up once rather than once
    ;; per frame. The byte offsets are unchanged, so the interleaving is too.
    (dotimes [c channels]
      (let [^js data (nth samples c)]
        (dotimes [i frames]
          (let [sample (* level (aget data i))]
            (.setInt16 view (+ 44 (* (+ (* i channels) c) 2))
                       (js/Math.round (* 32767 (max -1 (min 1 sample)))) true)))))
    (js/Uint8Array. bytes)))

(defn- wav-url [^js buffer]
  (js/URL.createObjectURL
   (js/Blob. #js [(wav-bytes buffer)] #js {:type "audio/wav"})))

(defn- fetch-ok! [url what]
  (-> (js/fetch url)
      (.then (fn [response]
               (when-not (.-ok response)
                 (throw (ex-info (str "audio track's " what " is missing")
                                 {:url url :status (.-status response)})))
               response))))

(defn- decode-bytes! [bytes]
  (.decodeAudioData (js/OfflineAudioContext. 1 1 44100) bytes))

(defn- source!
  "Promise of `[source {:buffer :fps}]` for an audio node's `:source`. Footage
  counts its frames at its own rate; a sound file has no frames of its own, so
  its `:fps` is nil and it counts in the document's."
  [{:keys [footage sound] :as source}]
  (if sound
    (-> (fetch-ok! (str "/api/sounds/" sound) "sound")
        (.then #(.json %))
        (.then #(fetch-ok! (.-audio %) "blob"))
        (.then #(.arrayBuffer %))
        (.then decode-bytes!)
        (.then (fn [buffer] [source {:buffer buffer}])))
    (-> (fetch-ok! (str "/api/footage/" footage) "footage")
        (.then #(.json %))
        (.then (fn [^js manifest]
                 (-> (fetch-ok! (.-audio manifest) "blob")
                     (.then #(.arrayBuffer %))
                     (.then decode-bytes!)
                     (.then (fn [buffer] [source {:buffer buffer :fps (.-fps manifest)}]))))))))

(defn- automate! [^js param channel start end fps factor default store]
  (let [channel (or channel (ch/framed default))
        sample (fn [f] (ch/value-at channel
                                   (if-let [{:keys [at rate]} (:sample-time channel)]
                                     (js/Math.floor (* rate (- f at))) f)
                                   store))]
    (.setValueAtTime param (* factor (sample start)) (/ start fps))
    (cond
      (:dense channel)
      (doseq [f (range (inc start) end)]
        (.setValueAtTime param (* factor (sample f)) (/ f fps)))

      (:animated? channel)
      (doseq [[f v] (sort-by key (:keys channel))
              :when (and (> f start) (< f end))]
        (if (= :linear (:interp channel))
          (.linearRampToValueAtTime param (* factor v) (/ f fps))
          (.setValueAtTime param (* factor v) (/ f fps)))))))

(defn tracks-of
  "The sounds symbol `sid` plays, including those inside what it places — see
  `nest/audio-tracks`. Playback mixes the open symbol's."
  [document sid]
  (nest/audio-tracks document sid))

(defn- render! [document sid sources store]
  (let [fps (:fps document)
        frames (clip/frames document sid)
        tracks (tracks-of document sid)
        output (js/OfflineAudioContext.
                2 (js/Math.ceil (* (/ frames fps) 44100)) 44100)]
    (doseq [track tracks]
      (let [[start end] (or (node/placed-span track) [0 frames])
            start (max 0 start)
            end (min frames end)
            {:keys [buffer fps] :or {fps (:fps track)}} (get sources (:source track))
            sound (.createBufferSource output)
            gain (.createGain output)
            pan (.createStereoPanner output)]
        (when (< start end)
          (set! (.-buffer sound) buffer)
          (set! (.-loop sound) (boolean (get-in track [:time :loop?])))
          (automate! (.-playbackRate sound)
                     (get-in track [:channels [:audio :rate]])
                     start end (:fps document) (* (or (get-in track [:time :rate]) 1) (/ (:fps document) fps)) 1 store)
          (automate! (.-gain gain)
                     (get-in track [:channels [:audio :gain]])
                     start end (:fps document) 1 1 store)
          (automate! (.-pan pan)
                     (get-in track [:channels [:audio :pan]])
                     start end (:fps document) 1 0 store)
          (.connect sound gain)
          (.connect gain pan)
          (.connect pan (.-destination output))
          (.start sound (/ start (:fps document)) (/ (node/local-frame track start) fps))
          (.stop sound (/ end (:fps document))))))
    (.startRendering output)))

(defn buffer!
  "Promise of the `AudioBuffer` one symbol's audio tracks mix down to, or nil
  when it has none.

  The raw product. `mix!` packages it as a WAV URL for the transport and
  `export/frames` packages it as WAV bytes in an archive; a muxer would take it as
  it is, which is why this is the function the others are written in terms of."
  [document sid store]
  (let [tracks (tracks-of document sid)]
    (if (empty? tracks)
      (js/Promise.resolve nil)
      (-> (js/Promise.all
           (into-array (map source! (distinct (map :source tracks)))))
          (.then (fn [pairs] (render! document sid (into {} (array-seq pairs)) store)))))))

(defn decode!
  "Promise of the `AudioBuffer` behind a URL. What a clip whose audio is a plain
  file rather than placed tracks exports."
  [url]
  (-> (js/fetch url)
      (.then (fn [^js response]
               (when-not (.-ok response)
                 (throw (ex-info "the clip's audio did not load"
                                 {:url url :status (.-status response)})))
               (.arrayBuffer response)))
      (.then decode-bytes!)))

(defn fit-buffer
  "Fit fallback audio to the open timeline, padding with silence or trimming.
  The buffer and clock must share a duration so looping wraps at the timeline's
  end rather than repeating a short soundtrack underneath a longer animation."
  [^js buffer seconds]
  (let [rate (.-sampleRate buffer)
        frames (max 1 (js/Math.ceil (* seconds rate)))]
    (if (= frames (.-length buffer))
      buffer
      (let [channels (.-numberOfChannels buffer)
            fitted (.createBuffer (js/OfflineAudioContext. channels 1 rate)
                                  channels frames rate)]
        (dotimes [c channels]
          (.set (.getChannelData fitted c)
                (.subarray (.getChannelData buffer c) 0 (min frames (.-length buffer)))))
        fitted))))

(defn clock-source!
  "Promise of `{:buffer :seconds}` — the audio the transport runs its clock on
  while symbol `sid` is open, and how long that clock is.

  Same order of preference as the WAV packaging in `clock!` below: the symbol's
  own placed tracks, mixed; the document's audio file, for the symbol the
  document opens on and only that one; and otherwise NO BUFFER AT ALL and the
  symbol's own length.

  THE SILENT CASE IS WHY THE DURATION IS RETURNED BESIDE THE BUFFER rather than
  read off it. A symbol with no sound still needs a clock exactly as long as it
  is, and `clock!` had to synthesize that silence and then ENCODE it, full
  length, so an element had a duration to report. There is nothing to decode for
  a symbol with no sound: saying how long it is answers the only question the
  silence was ever asked."
  [document sid fallback-url store]
  (-> (buffer! document sid store)
      (.then (fn [^js buffer]
               (cond
                 buffer
                 {:buffer buffer :seconds (.-duration buffer)}

                 (and fallback-url (= sid (clip/opens-on document)))
                 (-> (decode! fallback-url)
                     (.then (fn [b]
                              (let [seconds (/ (clip/frames document sid) (:fps document))]
                                {:buffer (fit-buffer b seconds) :seconds seconds}))))

                 :else
                 {:buffer nil
                  :seconds (/ (clip/frames document sid) (:fps document))})))))

(defn clock!
  "Promise of the URL the transport should play while symbol `sid` is open.

  THE ELEMENT BACKEND'S PACKAGING of `clock-source!`, kept while that backend is
  — see `arthur.clock`. Every cost this namespace had on open is in the two
  `wav-url` calls below.

  The frame is derived from the audio element and from nothing else, so every
  open symbol needs a sound exactly as long as it is. In order: its own placed
  tracks, mixed; the document's audio file, for the symbol the document opens on
  and only that one; and otherwise SILENCE of the symbol's length — a ten-frame
  symbol played against the whole take's soundtrack would run ten frames and then
  keep the clock going for minutes."
  [document sid fallback-url store]
  (-> (clock-source! document sid fallback-url store)
      (.then (fn [{:keys [buffer seconds]}]
               (wav-url (or buffer
                            (.createBuffer (js/OfflineAudioContext. 1 1 44100)
                                           1 (max 1 (js/Math.ceil (* 44100 seconds))) 44100)))))))
